import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ToastContainer } from '../../../backend-specs/components/Toast.jsx';

const SaveButton = ({ getComponent, backendSpecsActions, backendSpecsSelectors }) => {
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = toastIdRef.current++;
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const SaveSpecDialog = getComponent('SaveSpecDialog', true);

  const getCurrentSpec = useCallback(() => {
    const reduxSpec = backendSpecsSelectors?.selectCurrentSpec?.();
    if (reduxSpec?.id) {
      return { id: reduxSpec.id, name: reduxSpec.name };
    }
    return null;
  }, [backendSpecsSelectors]);

  const handleSaveClick = useCallback(
    async (event) => {
      event.preventDefault();
      const spec = getCurrentSpec();

      if (!spec?.id) {
        setIsSaveDialogOpen(true);
      } else {
        if (backendSpecsActions?.saveSpec) {
          setIsSaving(true);
          try {
            const result = await backendSpecsActions.saveSpec(spec.name, spec.id);
            if (result?.error) {
              addToast(result.meta?.errorMessage || 'Failed to save spec', 'error');
            } else {
              addToast(`Spec "${spec.name}" saved successfully`, 'success');
            }
          } finally {
            setIsSaving(false);
          }
        }
      }
    },
    [backendSpecsActions, addToast, getCurrentSpec]
  );

  const handleSaveDialogClose = useCallback(() => {
    setIsSaveDialogOpen(false);
  }, []);

  const handleSaveDialogSave = useCallback(
    async (specName) => {
      if (backendSpecsActions?.saveSpec) {
        setIsSaving(true);
        try {
          const existingSpec = getCurrentSpec();
          const result = await backendSpecsActions.saveSpec(specName, existingSpec?.id);
          if (result?.error) {
            addToast(result.meta?.errorMessage || 'Failed to save spec', 'error');
          } else if (result?.payload) {
            addToast(`Spec "${specName}" saved successfully`, 'success');
          }
        } finally {
          setIsSaving(false);
        }
      }
      setIsSaveDialogOpen(false);
    },
    [backendSpecsActions, addToast, getCurrentSpec]
  );

  const currentSpec = getCurrentSpec();
  const isNewSpec = !currentSpec?.id;

  if (!backendSpecsActions) return null;

  return (
    <>
      {SaveSpecDialog && (
        <SaveSpecDialog
          isOpen={isSaveDialogOpen}
          onClose={handleSaveDialogClose}
          onSave={handleSaveDialogSave}
          getComponent={getComponent}
          currentSpecName={currentSpec?.name || ''}
          isNewSpec={isNewSpec}
        />
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <button
        type="button"
        className={`save-button${isSaving ? ' save-button--saving' : ''}`}
        onClick={handleSaveClick}
        disabled={isSaving}
        title="Save spec to server"
      >
        {isSaving ? 'Saving\u2026' : 'Save'}
      </button>
    </>
  );
};

SaveButton.propTypes = {
  getComponent: PropTypes.func.isRequired,
  backendSpecsActions: PropTypes.object,
  backendSpecsSelectors: PropTypes.object,
};

SaveButton.defaultProps = {
  backendSpecsActions: null,
  backendSpecsSelectors: null,
};

export default SaveButton;
